import React from 'react';
import { Avatar, Box, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@material-ui/core';
import { BusinessOutlined, DescriptionOutlined, PersonOutline } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import { IOnboardingAccount } from '../../../../../Api/AptorApi';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { useStyles } from './Onboarding.styles';
import { hasText } from './OnboardingDraft';

const companyUnitsByItem = <T extends { id: number; companyUnitName?: string }>(items: T[]) => {
  const unitsById = new Map<number, Set<string>>();
  items.forEach(item => {
    if (!item.companyUnitName) return;
    const units = unitsById.get(item.id) || new Set<string>();
    units.add(item.companyUnitName);
    unitsById.set(item.id, units);
  });
  return unitsById;
};

export const OnboardingReview = ({ account }: { account: IOnboardingAccount }) => {
  const { formatMessage: t } = useIntl();
  const classes = useStyles();
  const legislation = account.legislation || [];
  const legislationUnits = companyUnitsByItem(legislation);
  const legislationRows = Array.from(new Map(legislation.map(law => [law.id, law])).values()).map(law => ({
    key: String(law.id),
    name: `${law.number} – ${law.name}`,
    detail: <strong>{Array.from(legislationUnits.get(law.id) || []).join(', ')}</strong>,
  }));
  const requirements = account.otherRequirements || [];
  const requirementUnits = companyUnitsByItem(requirements);
  const requirementRows = Array.from(new Map(requirements.map(item => [item.id, item])).values()).map(item => ({
    key: String(item.id), name: item.name,
    detail: <strong>{Array.from(requirementUnits.get(item.id) || []).join(', ')}</strong>,
  }));
  const companyUnits = account.companyUnitDetails || [];
  const companyNameKey = (account.customerName || '').trim().toLocaleLowerCase();
  const companyNameIsUnit = companyUnits.some(unit => unit.name.trim().toLocaleLowerCase() === companyNameKey);
  const companyUnitRows = [
    ...(account.customerName && !companyNameIsUnit ? [{ key: 'company-name', name: account.customerName }] : []),
    ...companyUnits.map(unit => ({ key: String(unit.id), name: unit.name })),
  ];
  const legislationNotes = new Map<string, { id: string; name: string; units: Set<string>; note: string }>();
  legislation.filter(law => hasText(law.note || '')).forEach(law => {
    const key = `${law.id}:${law.note}`;
    const row = legislationNotes.get(key) || { id: key, name: `${law.number} – ${law.name}`, units: new Set<string>(), note: law.note || '' };
    if (law.companyUnitName) row.units.add(law.companyUnitName);
    legislationNotes.set(key, row);
  });
  const requirementNotes = new Map<string, { id: string; name: string; units: Set<string>; note: string }>();
  requirements.filter(item => hasText(item.note || '')).forEach(item => {
    const key = `${item.id}:${item.note}`;
    const row = requirementNotes.get(key) || { id: key, name: item.name, units: new Set<string>(), note: item.note || '' };
    if (item.companyUnitName) row.units.add(item.companyUnitName);
    requirementNotes.set(key, row);
  });
  const sections = [
    { title: 'onboarding.step.units', icon: <BusinessOutlined />, rows: companyUnitRows },
    { title: 'onboarding.step.permissions', icon: <PersonOutline />, rows: [{ key: 'admin', name: `${account.customerAdminName} <${account.customerAdminEmail}>`, detail: <strong>{(account.adminCompanyUnits || []).map(unit => unit.name).join(', ')}</strong> }] },
    { title: 'onboarding.step.laws', icon: <DescriptionOutlined />, rows: legislationRows },
    { title: 'onboarding.step.notes', icon: <DescriptionOutlined />, rows: Array.from(legislationNotes.values()).map(row => ({ key: row.id, name: row.name, detail: <><Typography variant="body2"><strong>{Array.from(row.units).join(', ')}</strong></Typography><HtmlView value={row.note} /></> })) },
    { title: 'onboarding.step.requirements', icon: <DescriptionOutlined />, rows: requirementRows },
    { title: 'onboarding.step.requirement-notes', icon: <DescriptionOutlined />, rows: Array.from(requirementNotes.values()).map(row => ({ key: row.id, name: row.name, detail: <><Typography variant="body2"><strong>{Array.from(row.units).join(', ')}</strong></Typography><HtmlView value={row.note} /></> })) },
  ];
  return <>
    {sections.filter(section => section.rows.length > 0).map(section => <Box key={section.title} className={classes.reviewSection}>
      <Typography variant="subtitle1">{t({ id: section.title })}</Typography>
      <List disablePadding>
        {section.rows.map((row: { key: string; name: string; detail?: React.ReactNode }) => <ListItem key={row.key} className={classes.reviewItem}>
          <ListItemAvatar><Avatar variant="rounded" className={classes.reviewIcon}>{section.icon}</Avatar></ListItemAvatar>
          <ListItemText primary={<Typography variant="body2">{row.name}</Typography>} disableTypography secondary={row.detail && <Box>{row.detail}</Box>} />
        </ListItem>)}
      </List>
    </Box>)}
  </>;
};
